/* Schriften für das Profil-PDF laden (Instrument Sans und Inter, public/fonts/*.ttf).
 *
 * Sie werden NUR beim Erzeugen eines Profil-PDFs geladen — nicht beim Start der App und nicht im
 * Service-Worker-Precache (siehe docs/profil-pdf-schriften.md). Der Service Worker legt abgerufene Dateien
 * ohnehin im Laufzeit-Cache ab, ein zweiter Export läuft also auch offline mit denselben Schriften.
 *
 * RÜCKFALL: Lässt sich eine Schrift nicht laden (Netz, offline, Fehler, Zeitlimit), liefert loadProfileFonts
 * `null`; das PDF entsteht dann wie bisher mit Helvetica und der Transliteration pdfText(). Wegen der Schrift
 * scheitert nie ein Export. Alles oder nichts: ein halber Satz würde ein uneinheitliches Blatt ergeben.
 */

/** Zeitlimit für das Laden aller Schriften zusammen (Millisekunden). */
export const FONT_TIMEOUT_MS = 8000;

/** Dateien je Rolle. Schlüssel = Rolle im Renderer, family/style = Name in jsPDF. */
export const FONT_FILES = {
  display: { file: 'InstrumentSans-Regular.ttf', family: 'InstrumentSans', style: 'normal' },
  displayBold: { file: 'InstrumentSans-Bold.ttf', family: 'InstrumentSansBold', style: 'normal' },
  text: { file: 'Inter-Regular.ttf', family: 'Inter', style: 'normal' },
  textMedium: { file: 'Inter-Medium.ttf', family: 'InterMedium', style: 'normal' },
  textSemi: { file: 'Inter-SemiBold.ttf', family: 'InterSemi', style: 'normal' },
};

/**
 * Unicode-Bereiche, die die Dateien in public/fonts enthalten (so wurden sie erzeugt): Latin, Latin Extended-A/B,
 * kombinierende Akzente, Latin Extended Additional (ọ ẹ ṣ ị ụ …), Satzzeichen, ₦ €, Pfeile, Minus.
 * Zeichen außerhalb gehen über pdfText() auf den Grundbuchstaben — als Absicherung.
 */
export const FONT_RANGES = [
  [0x20, 0x7e], [0xa0, 0x24f], [0x2b0, 0x2ff], [0x300, 0x36f], [0x1e00, 0x1eff],
  [0x2010, 0x2027], [0x2030, 0x205e], [0x20a6, 0x20a6], [0x20ac, 0x20ac], [0x2190, 0x2193], [0x2212, 0x2212],
];

export const inFontRanges = (cp) => FONT_RANGES.some(([a, b]) => cp >= a && cp <= b);

function toBase64(buffer) {
  const bytes = new Uint8Array(buffer);
  let bin = '';
  for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(bin);
}

/**
 * @param {{ fetchFn?: Function, base?: string, timeoutMs?: number }} [opts]
 * @returns {Promise<Record<string, { file: string, family: string, style: string, data: string }> | null>}
 *   Rolle -> Schrift (data = Base64) oder null bei jedem Fehler
 */
export async function loadProfileFonts({ fetchFn = globalThis.fetch, base = '/fonts/', timeoutMs = FONT_TIMEOUT_MS } = {}) {
  if (typeof fetchFn !== 'function') return null;
  const controller = typeof AbortController === 'function' ? new AbortController() : null;
  let timer;
  const limit = new Promise((_, reject) => {
    timer = setTimeout(() => {
      if (controller) controller.abort();
      reject(new Error('fontTimeout'));
    }, timeoutMs);
  });
  const load = Promise.all(
    Object.entries(FONT_FILES).map(async ([role, def]) => {
      const res = await fetchFn(`${base}${def.file}`, controller ? { signal: controller.signal } : undefined);
      if (!res || !res.ok) throw new Error(`fontLoadFailed:${def.file}`);
      const buf = await res.arrayBuffer();
      if (!buf || buf.byteLength < 1000) throw new Error(`fontEmpty:${def.file}`);
      return [role, { ...def, data: toBase64(buf) }];
    })
  );
  try {
    return Object.fromEntries(await Promise.race([load, limit]));
  } catch (e) {
    console.error('[profilePdf] Schriften nicht verfügbar, Rückfall auf Helvetica:', e && e.message);
    load.catch(() => {}); // abgebrochene Abrufe nicht als unbehandelt melden
    return null;
  } finally {
    clearTimeout(timer);
  }
}
